import { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Components
import MarkdownContainer from '../../../atoms/MarkdownContainer/MarkdownContainer';
// Utils
import { parseFontAwesomeClass } from '../../../../utils/fontawesome/config';
import cn from '../../../../utils/cn';
import { normalizeUrl } from '../../../../utils/url';

// Styles
import styles from './ListCell.module.css';
import commonStyles from '../TableCellCommon.module.css';

/**
 * List cell component for displaying a list of items with icons and colors in table cells
 * @param {object} props
 * @param {Array<{icon?: string, iconColor?: string, lib?: string, value?: string, label?: string, backgroundColor?: string, path?: string}>} [props.value] - Array of list items
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.placeholder='-'] - Placeholder for empty values
 * @param {Align} [props.align='left'] - Text alignment
 * @param {VerticalAlign} [props.verticalAlign='middle'] - Vertical alignment
 * @param {boolean} [props.bold=false] - Text in bold
 * @param {boolean} [props.italic=false] - Text in italic
 * @param {boolean} [props.strikeThrough=false] - Text with strike-through
 * @param {boolean} [props.underline=false] - Text underlined
 * @returns {JSX.Element}
 */
function ListCell({
  value = [],
  className = '',
  placeholder = '-',
  align = 'left',
  verticalAlign = 'middle',
  bold = false,
  italic = false,
  strikeThrough = false,
  underline = false,
}) {
  const items = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const cellClassName = cn([
    styles['bullet-cell'],
    bold && styles['bullet-cell-column-bold'],
    className,
    bold && commonStyles['cell-bold'],
    italic && commonStyles['cell-italic'],
    strikeThrough && commonStyles['cell-strike-through'],
    underline && commonStyles['cell-underline'],
    align && commonStyles[`cell-align-${align}`],
    verticalAlign && commonStyles[`cell-vertical-align-${verticalAlign}`],
  ]);

  if (items.length === 0) {
    return (
      <div className={cellClassName}>
        {placeholder ? (
          <span className={styles['bullet-cell-empty']}>{placeholder}</span>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cellClassName}>
      <div className={styles['bullet-cell-list']}>
        {items.map((item, index) => {
          const label = item.label || item.lib || '';
          const iconDefinition = item?.icon ? parseFontAwesomeClass(item.icon) : null;
          const iconColor = item?.iconColor;
          const itemBackgroundColor = item?.backgroundColor || 'transparent';
          const path = normalizeUrl(item?.path) || '';

          const containerClassName = cn([
            styles['bullet-cell-item-container'],
            styles['bullet-cell-item-container-background-color'],
          ]);

          const containerContent = (
            <>
              {iconDefinition && iconColor ? (
                <FontAwesomeIcon icon={/** @type {any} */(iconDefinition)} />
              ) : null}
              <MarkdownContainer
                content={label}
                className={styles['bullet-cell-label']}
              />
            </>
          );

          return (
            <div key={item.value || index} className={styles['bullet-multi-value']}>
              {path ? (
                <a
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={containerClassName}
                  style={{ color: iconColor, backgroundColor: itemBackgroundColor }}
                >
                  {containerContent}
                </a>
              ) : (
                <div
                  className={containerClassName}
                  style={{ color: iconColor, backgroundColor: itemBackgroundColor }}
                >
                  {containerContent}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ListCell;
